import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/seo/SeoHead";
import { CMS_PREVIEW_EVENT } from "@/cms/previewUtils";
import { getValueAtPath, setValueAtPath } from "@/cms/pathUtils";
import { useSiteContent } from "@/cms/SiteContentContext";
import { isSupabaseConfigured, SITE_MEDIA_BUCKET, supabase } from "@/lib/supabase";
import type { Session } from "@supabase/supabase-js";
import type { BlogPost, EditableFieldType, SiteContent } from "@/cms/types";

type EditorSelection = {
  path: string;
  type: EditableFieldType | string;
};

type AdminMode = "preview" | "blog";

const PREVIEW_PATHS = [
  { label: "Ana Sayfa", path: "/" },
  { label: "Hizmetler", path: "/hizmetler" },
  { label: "Hakkımızda", path: "/hakkimizda" },
  { label: "Yorumlar", path: "/yorumlar" },
  { label: "İletişim", path: "/iletisim" },
  { label: "Blog", path: "/blog" },
];

function slugify(input: string): string {
  return input
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function createBlogPost(existingPosts: BlogPost[]): BlogPost {
  const nextIndex = existingPosts.length + 1;
  const now = new Date();
  const dateLabel = now.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const uniqueId = `blog-${Date.now()}-${nextIndex}`;
  const baseSlug = `yeni-blog-${nextIndex}`;
  const usedSlugs = new Set(existingPosts.map((post) => post.slug));
  let slug = baseSlug;
  let suffix = 2;
  while (usedSlugs.has(slug)) {
    slug = `${baseSlug}-${suffix}`;
    suffix += 1;
  }

  return {
    id: uniqueId,
    slug,
    title: `Yeni Blog Yazisi ${nextIndex}`,
    excerpt: "Yeni blog yazisinin kisa ozeti.",
    category: "Genel",
    date: dateLabel,
    publishedAt: now.toISOString(),
    readTime: "5 dk",
    image: "/images/card.png",
    content: ["Ilk paragrafi buradan duzenleyin."],
    highlights: ["One cikan not"],
    faqs: [{ q: "Soru?", a: "Cevap." }],
  };
}

function inferType(value: unknown): EditableFieldType {
  if (Array.isArray(value)) return "array";
  if (typeof value === "number") return "number";
  if (typeof value === "string") {
    if (/\.(mp4|webm|mov|m4v|ogg)(\?.*)?$/i.test(value)) return "video";
    if (value.startsWith("http") || value.startsWith("/images/")) return "image";
    if (value.includes("\n") || value.length > 120) return "textarea";
    return "text";
  }
  if (value && typeof value === "object") return "object";
  return "text";
}

function inferTypeFromPath(path: string): EditableFieldType | null {
  const normalized = path.toLowerCase();
  const videoLike =
    normalized.endsWith(".video") ||
    normalized.endsWith(".videosrc") ||
    normalized.includes(".videos.");
  const imageLike =
    normalized.endsWith(".img") ||
    normalized.endsWith(".image") ||
    normalized.endsWith(".imagesrc") ||
    normalized.endsWith(".logosrc") ||
    normalized.endsWith(".partnerlogo") ||
    normalized.endsWith(".logo") ||
    normalized.includes(".images.");
  if (videoLike) return "video";
  if (imageLike) return "image";
  return null;
}

function isImageFieldName(fieldName: string): boolean {
  const key = fieldName.toLowerCase();
  return (
    key === "img" ||
    key === "image" ||
    key === "logo" ||
    key.endsWith("imagesrc") ||
    key.endsWith("logosrc") ||
    key.endsWith("partnerlogo")
  );
}

function isVideoFieldName(fieldName: string): boolean {
  const key = fieldName.toLowerCase();
  return (
    key === "video" ||
    key.endsWith("videosrc")
  );
}

function emptyObjectFromSample(sample: Record<string, unknown>, nextIndex: number): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  const isBlogPostSample =
    "publishedAt" in sample &&
    "readTime" in sample &&
    "faqs" in sample &&
    "highlights" in sample;

  for (const [key, value] of Object.entries(sample)) {
    if (isBlogPostSample && key === "id") {
      next[key] = `blog-${Date.now()}-${nextIndex}`;
      continue;
    }
    if (isBlogPostSample && key === "slug") {
      next[key] = `yeni-blog-${nextIndex}`;
      continue;
    }
    if (isBlogPostSample && key === "title") {
      next[key] = `Yeni Blog Yazısı ${nextIndex}`;
      continue;
    }
    if (isBlogPostSample && key === "excerpt") {
      next[key] = "Yeni blog yazısı özeti.";
      continue;
    }
    if (isBlogPostSample && key === "category") {
      next[key] = "Genel";
      continue;
    }
    if (isBlogPostSample && key === "date") {
      next[key] = new Date().toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
      continue;
    }
    if (isBlogPostSample && key === "publishedAt") {
      next[key] = new Date().toISOString();
      continue;
    }
    if (isBlogPostSample && key === "readTime") {
      next[key] = "5 dk";
      continue;
    }
    if (isBlogPostSample && key === "image") {
      next[key] = "/images/card.png";
      continue;
    }
    if (isBlogPostSample && key === "content") {
      next[key] = ["Yeni blog içeriğini buradan düzenleyebilirsiniz."];
      continue;
    }
    if (isBlogPostSample && key === "highlights") {
      next[key] = ["Öne çıkan not"];
      continue;
    }
    if (isBlogPostSample && key === "faqs") {
      next[key] = [{ q: "Soru?", a: "Cevap." }];
      continue;
    }

    if (key === "id") {
      next[key] = String(nextIndex).padStart(2, "0");
      continue;
    }
    if (key === "slug") {
      next[key] = `yeni-kart-${nextIndex}`;
      continue;
    }
    if (key === "title") {
      next[key] = `Yeni Kart ${nextIndex}`;
      continue;
    }
    if (key === "icon") {
      next[key] = "ri-tools-line";
      continue;
    }
    if (Array.isArray(value)) {
      next[key] = [];
      continue;
    }
    if (typeof value === "number") {
      next[key] = 0;
      continue;
    }
    if (typeof value === "boolean") {
      next[key] = false;
      continue;
    }
    if (value && typeof value === "object") {
      next[key] = {};
      continue;
    }
    next[key] = "";
  }
  return next;
}

function getEmptyArrayItem(sample: unknown, nextIndex: number): unknown {
  if (sample && typeof sample === "object" && !Array.isArray(sample)) {
    return emptyObjectFromSample(sample as Record<string, unknown>, nextIndex);
  }
  if (typeof sample === "number") return 0;
  return "";
}

function findNearestArrayPath(content: SiteContent, path: string): string | null {
  const parts = path.split(".");
  for (let i = parts.length; i >= 1; i -= 1) {
    const candidate = parts.slice(0, i).join(".");
    const value = getValueAtPath(content, candidate);
    if (Array.isArray(value)) return candidate;
  }
  return null;
}

function LoginForm({
  onLogin,
  loading,
}: {
  onLogin: (email: string, password: string) => Promise<string | null>;
  loading: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    const maybeError = await onLogin(email, password);
    if (maybeError) setError(maybeError);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
      <form className="w-full max-w-md bg-zinc-900 border border-zinc-800 p-6" onSubmit={submit}>
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Rajdhani, sans-serif" }}>
          Yönetim Paneli
        </h1>
        <p className="text-zinc-400 text-sm mb-6">Supabase Auth hesabınızla giriş yapın.</p>
        <label className="block text-sm mb-2">E-posta</label>
        <input
          className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 mb-4"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          required
        />
        <label className="block text-sm mb-2">Şifre</label>
        <input
          className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2 mb-5"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          type="password"
          required
        />
        {error ? <p className="text-red-400 text-sm mb-4">{error}</p> : null}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 px-4 py-2 font-semibold"
        >
          {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
        </button>
        <Link to="/" className="mt-4 inline-block text-xs text-zinc-400 hover:text-zinc-200">
          Siteye dön
        </Link>
      </form>
    </div>
  );
}

function ValueEditor({
  value,
  type,
  onChange,
  onUpload,
}: {
  value: unknown;
  type: EditableFieldType;
  onChange: (next: unknown) => void;
  onUpload: (file: File) => Promise<string>;
}) {
  const arrayValue = Array.isArray(value) ? value : null;
  const objectValue = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (type === "number") {
    return (
      <input
        type="number"
        className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
        value={typeof value === "number" ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    );
  }

  if (type === "textarea") {
    return (
      <textarea
        className="w-full min-h-40 bg-zinc-950 border border-zinc-700 px-3 py-2"
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (type === "image" || type === "video") {
    const isVideo = type === "video";
    return (
      <div className="space-y-3">
        {typeof value === "string" && value ? (
          <div className="bg-zinc-950 border border-zinc-700 p-3">
            {isVideo ? (
              <video src={value} controls className="w-full max-h-64 bg-zinc-900" />
            ) : (
              <img src={value} alt="Seçili görsel" className="w-full max-h-64 object-contain bg-zinc-900" />
            )}
          </div>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept={isVideo ? "video/*" : "image/*"}
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const url = await onUpload(file);
            if (url) onChange(url);
            e.currentTarget.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold"
        >
          {isVideo ? "Video Yükle" : "Görsel Yükle"}
        </button>
        <label className="block text-xs text-zinc-400">İstersen URL de girebilirsin</label>
        <input
          className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isVideo ? "Video URL" : "Görsel URL"}
        />
      </div>
    );
  }

  if (type === "array" && arrayValue) {
    const sample = arrayValue[0];
    return (
      <div className="space-y-3">
        {arrayValue.map((item, idx) => (
          <div key={idx} className="bg-zinc-950 border border-zinc-700 p-3 space-y-2">
            {item && typeof item === "object" ? (
              <div className="space-y-3">
                {Object.entries(item as Record<string, unknown>).map(([key, fieldValue]) => {
                  const keyIsImage = isImageFieldName(key);
                  const keyIsVideo = isVideoFieldName(key);
                  const keyIsMedia = keyIsImage || keyIsVideo;
                  return (
                    <div key={`${idx}-${key}`} className="space-y-2">
                      <label className="text-xs text-zinc-400">{key}</label>
                      {keyIsMedia ? (
                        <div className="space-y-2">
                          {typeof fieldValue === "string" && fieldValue ? (
                            <div className="bg-zinc-900 border border-zinc-700 p-2">
                              {keyIsVideo ? (
                                <video src={fieldValue} controls className="w-full max-h-44" />
                              ) : (
                                <img src={fieldValue} alt={`${key}-${idx}`} className="w-full max-h-44 object-contain" />
                              )}
                            </div>
                          ) : null}
                          <input
                            className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2"
                            value={typeof fieldValue === "string" ? fieldValue : ""}
                            onChange={(e) => {
                              const next = [...arrayValue];
                              const currentItem = { ...(next[idx] as Record<string, unknown>) };
                              currentItem[key] = e.target.value;
                              next[idx] = currentItem;
                              onChange(next);
                            }}
                            placeholder={keyIsVideo ? "Video URL" : "Görsel URL"}
                          />
                          <label
                            htmlFor={`cms-array-upload-${idx}-${key}`}
                            className="inline-flex items-center bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs cursor-pointer"
                          >
                            {keyIsVideo ? "Video Yükle" : "Görsel Yükle"}
                          </label>
                          <input
                            id={`cms-array-upload-${idx}-${key}`}
                            type="file"
                            accept={keyIsVideo ? "video/*" : "image/*"}
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const url = await onUpload(file);
                              if (!url) return;
                              const next = [...arrayValue];
                              const currentItem = { ...(next[idx] as Record<string, unknown>) };
                              currentItem[key] = url;
                              next[idx] = currentItem;
                              onChange(next);
                              e.currentTarget.value = "";
                            }}
                          />
                        </div>
                      ) : typeof fieldValue === "number" ? (
                        <input
                          type="number"
                          className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2"
                          value={fieldValue}
                          onChange={(e) => {
                            const next = [...arrayValue];
                            const currentItem = { ...(next[idx] as Record<string, unknown>) };
                            currentItem[key] = Number(e.target.value);
                            next[idx] = currentItem;
                            onChange(next);
                          }}
                        />
                      ) : Array.isArray(fieldValue) ? (
                        <textarea
                          className="w-full min-h-28 bg-zinc-900 border border-zinc-700 px-3 py-2 text-xs"
                          value={JSON.stringify(fieldValue, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              if (!Array.isArray(parsed)) return;
                              const next = [...arrayValue];
                              const currentItem = { ...(next[idx] as Record<string, unknown>) };
                              currentItem[key] = parsed;
                              next[idx] = currentItem;
                              onChange(next);
                            } catch {
                              // Ignore parse errors while typing.
                            }
                          }}
                        />
                      ) : fieldValue && typeof fieldValue === "object" ? (
                        <textarea
                          className="w-full min-h-28 bg-zinc-900 border border-zinc-700 px-3 py-2 text-xs"
                          value={JSON.stringify(fieldValue, null, 2)}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;
                              const next = [...arrayValue];
                              const currentItem = { ...(next[idx] as Record<string, unknown>) };
                              currentItem[key] = parsed;
                              next[idx] = currentItem;
                              onChange(next);
                            } catch {
                              // Ignore parse errors while typing.
                            }
                          }}
                        />
                      ) : (
                        <input
                          className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2"
                          value={typeof fieldValue === "string" ? fieldValue : JSON.stringify(fieldValue)}
                          onChange={(e) => {
                            const next = [...arrayValue];
                            const currentItem = { ...(next[idx] as Record<string, unknown>) };
                            currentItem[key] = e.target.value;
                            next[idx] = currentItem;
                            onChange(next);
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <input
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2"
                value={String(item ?? "")}
                onChange={(e) => {
                  const next = [...arrayValue];
                  next[idx] = e.target.value;
                  onChange(next);
                }}
              />
            )}
            <button
              type="button"
              onClick={() => {
                const next = arrayValue.filter((_, i) => i !== idx);
                onChange(next);
              }}
              className="text-xs text-red-400 hover:text-red-300"
            >
              Öğeyi Sil
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...arrayValue, getEmptyArrayItem(sample, arrayValue.length + 1)])}
          className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
        >
          Yeni Öğe Ekle
        </button>
      </div>
    );
  }

  if (type === "object" && objectValue) {
    return (
      <textarea
        className="w-full min-h-64 bg-zinc-950 border border-zinc-700 px-3 py-2 text-xs"
        value={JSON.stringify(objectValue, null, 2)}
        onChange={(e) => {
          try {
            onChange(JSON.parse(e.target.value));
          } catch {
            // Ignore parse errors while typing.
          }
        }}
      />
    );
  }

  return (
    <input
      className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export default function AdminPage() {
  const { content, loading, setContent, saveContent } = useSiteContent();
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [previewPath, setPreviewPath] = useState("/");
  const [mode, setMode] = useState<AdminMode>("preview");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState<SiteContent>(content);
  const [isDirty, setIsDirty] = useState(false);
  const [selection, setSelection] = useState<EditorSelection | null>(null);
  const [editingValue, setEditingValue] = useState<unknown>(null);
  const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const blogImageInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraftContent(content);
  }, [content]);

  useEffect(() => {
    if (mode === "blog") setSelection(null);
  }, [mode]);

  const postPreviewPatch = useCallback(
    (nextContent: SiteContent) => {
      const frame = iframeRef.current;
      if (!frame?.contentWindow) return;
      frame.contentWindow.postMessage(
        {
          source: "cms-admin-patch",
          content: nextContent,
        },
        window.location.origin,
      );
    },
    [],
  );

  const verifyAdmin = useCallback(async (activeSession: Session | null) => {
    if (!activeSession || !supabase) {
      setAuthorized(false);
      return;
    }
    const { data } = await supabase
      .from("admin_users")
      .select("user_id")
      .eq("user_id", activeSession.user.id)
      .maybeSingle();
    setAuthorized(Boolean(data?.user_id));
  }, []);

  useEffect(() => {
    if (!supabase) {
      setAuthLoading(false);
      return;
    }

    const init = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      await verifyAdmin(data.session);
      setAuthLoading(false);
    };
    void init();

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      void verifyAdmin(nextSession);
    });

    return () => authListener.subscription.unsubscribe();
  }, [verifyAdmin]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!event.data || event.data.source !== CMS_PREVIEW_EVENT) return;
      const payload = event.data.payload as EditorSelection | undefined;
      if (!payload?.path) return;
      const value = getValueAtPath(draftContent, payload.path);
      setSelection(payload);
      setEditingValue(value ?? "");
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [draftContent]);

  const doLogin = async (email: string, password: string) => {
    if (!supabase) return "Supabase bağlantısı bulunamadı.";
    setLoginLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoginLoading(false);
    if (error) return error.message;
    return null;
  };

  const doLogout = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  };

  const selectedType = useMemo(() => {
    if (!selection) return "text";
    if (selection.type && selection.type !== "text") return selection.type as EditableFieldType;
    const fromPath = inferTypeFromPath(selection.path);
    if (fromPath) return fromPath;
    return inferType(editingValue);
  }, [selection, editingValue]);

  const nearestArrayPath = useMemo(() => {
    if (!selection) return null;
    return findNearestArrayPath(draftContent, selection.path);
  }, [draftContent, selection]);

  const blogPosts = draftContent.blog.posts;
  const selectedBlogIndex = useMemo(() => {
    if (!blogPosts.length) return -1;
    const index = blogPosts.findIndex((post) => post.id === selectedBlogId);
    return index >= 0 ? index : 0;
  }, [blogPosts, selectedBlogId]);
  const selectedBlogPost = selectedBlogIndex >= 0 ? blogPosts[selectedBlogIndex] : null;

  useEffect(() => {
    if (!blogPosts.length) {
      if (selectedBlogId !== null) setSelectedBlogId(null);
      return;
    }
    const exists = selectedBlogId ? blogPosts.some((post) => post.id === selectedBlogId) : false;
    if (!exists) setSelectedBlogId(blogPosts[0].id);
  }, [blogPosts, selectedBlogId]);

  const applyDraftContent = useCallback(
    (nextContent: SiteContent) => {
      setDraftContent(nextContent);
      setIsDirty(true);
      postPreviewPatch(nextContent);
    },
    [postPreviewPatch],
  );

  const applyEditorValue = () => {
    if (!selection) return;
    const next = setValueAtPath(draftContent, selection.path, editingValue);
    applyDraftContent(next);
    setSelection(null);
  };

  const openNearestArrayEditor = () => {
    if (!nearestArrayPath) return;
    const arrayValue = getValueAtPath(draftContent, nearestArrayPath);
    setSelection({
      path: nearestArrayPath,
      type: "array",
    });
    setEditingValue(Array.isArray(arrayValue) ? arrayValue : []);
  };

  const openEditorByPath = (path: string, type: EditableFieldType) => {
    const value = getValueAtPath(draftContent, path);
    setSelection({ path, type });
    if (type === "array") {
      setEditingValue(Array.isArray(value) ? value : []);
      return;
    }
    setEditingValue(value ?? "");
  };

  const uploadImage = async (file: File): Promise<string> => {
    if (!supabase) return "";
    const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, "-");
    const path = `site/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from(SITE_MEDIA_BUCKET).upload(path, file, { upsert: true });
    if (error) {
      setStatusMessage(`Yükleme hatası: ${error.message}`);
      return "";
    }
    const { data } = supabase.storage.from(SITE_MEDIA_BUCKET).getPublicUrl(path);
    return data.publicUrl;
  };

  const replaceBlogPosts = useCallback(
    (posts: BlogPost[]) => {
      const nextContent: SiteContent = {
        ...draftContent,
        blog: {
          ...draftContent.blog,
          posts,
        },
      };
      applyDraftContent(nextContent);
    },
    [applyDraftContent, draftContent],
  );

  const updateSelectedBlog = useCallback(
    (updater: (current: BlogPost) => BlogPost) => {
      if (selectedBlogIndex < 0) return;
      const nextPosts = [...blogPosts];
      nextPosts[selectedBlogIndex] = updater(nextPosts[selectedBlogIndex]);
      replaceBlogPosts(nextPosts);
    },
    [blogPosts, replaceBlogPosts, selectedBlogIndex],
  );

  const addBlogPost = () => {
    const created = createBlogPost(blogPosts);
    const nextPosts = [...blogPosts, created];
    replaceBlogPosts(nextPosts);
    setSelectedBlogId(created.id);
    setMode("blog");
  };

  const deleteSelectedBlog = () => {
    if (selectedBlogIndex < 0 || !selectedBlogPost) return;
    const nextPosts = blogPosts.filter((post) => post.id !== selectedBlogPost.id);
    replaceBlogPosts(nextPosts);
    if (nextPosts.length) {
      setSelectedBlogId(nextPosts[Math.max(0, selectedBlogIndex - 1)]?.id ?? nextPosts[0].id);
    } else {
      setSelectedBlogId(null);
    }
  };

  const uploadSelectedBlogImage = async (file: File) => {
    const url = await uploadImage(file);
    if (!url) return;
    updateSelectedBlog((current) => ({ ...current, image: url }));
  };

  const saveAll = async () => {
    const result = await saveContent(draftContent);
    setStatusMessage(result.message);
    if (result.ok) {
      setContent(draftContent);
      setIsDirty(false);
    }
  };

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-8">
        <SeoHead title="Yönetim Paneli" path="/admin" noindex />
        <h1 className="text-2xl font-bold mb-3">Supabase Yapılandırması Gerekli</h1>
        <p className="text-zinc-400 text-sm">
          `.env.local` içinde `VITE_SUPABASE_URL` ve `VITE_SUPABASE_ANON_KEY` alanlarını doldurun.
        </p>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 p-8">
        <SeoHead title="Yönetim Paneli" path="/admin" noindex />
        Yükleniyor...
      </div>
    );
  }

  if (!session) {
    return <LoginForm onLogin={doLogin} loading={loginLoading} />;
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6">
        <SeoHead title="Yönetim Paneli" path="/admin" noindex />
        <div className="max-w-lg bg-zinc-900 border border-zinc-800 p-6">
          <h1 className="text-2xl font-bold mb-3">Bu kullanıcı yönetici değil</h1>
          <p className="text-zinc-400 text-sm mb-5">
            `admin_users` tablosuna bu hesabın `user_id` değeri eklenmeden panel düzenleme yetkisi verilemez.
          </p>
          <button
            type="button"
            className="bg-zinc-800 hover:bg-zinc-700 px-4 py-2 text-sm"
            onClick={() => {
              void doLogout();
            }}
          >
            Çıkış Yap
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-zinc-950 text-zinc-100 flex">
      <SeoHead title="Yönetim Paneli" path="/admin" noindex />
      <aside className="w-[420px] border-r border-zinc-800 p-4 overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            Yönetim Paneli
          </h1>
          <button onClick={() => void doLogout()} className="text-xs text-zinc-400 hover:text-zinc-100">
            Çıkış
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-5">
          <button
            type="button"
            onClick={() => setMode("preview")}
            className={`px-3 py-2 text-sm ${mode === "preview" ? "bg-red-600 hover:bg-red-700" : "bg-zinc-800 hover:bg-zinc-700"}`}
          >
            Canli Editor
          </button>
          <button
            type="button"
            onClick={() => setMode("blog")}
            className={`px-3 py-2 text-sm ${mode === "blog" ? "bg-red-600 hover:bg-red-700" : "bg-zinc-800 hover:bg-zinc-700"}`}
          >
            Blog Yonetimi
          </button>
        </div>

        <div className="space-y-2 mb-5">
          <button
            onClick={saveAll}
            className="w-full bg-red-600 hover:bg-red-700 px-4 py-2 font-semibold"
          >
            {isDirty ? "Degisiklikleri Kaydet" : "Kaydet"}
          </button>
          {statusMessage ? <p className="text-xs text-zinc-400">{statusMessage}</p> : null}
        </div>

        {mode === "preview" ? (
          <>
            <div className="space-y-3 mb-5">
              <label className="block text-xs text-zinc-400">Onizleme Sayfasi</label>
              <select
                className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2"
                value={previewPath}
                onChange={(e) => setPreviewPath(e.target.value)}
              >
                {PREVIEW_PATHS.map((item) => (
                  <option key={item.path} value={item.path}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2 mb-5">
              <button
                type="button"
                onClick={() => openEditorByPath("services.items", "array")}
                className="w-full bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
              >
                Hizmet Kartlari (Ekle/Sil)
              </button>
              <button
                type="button"
                onClick={() => openEditorByPath("services.hero.imageSrc", "image")}
                className="w-full bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
              >
                Hizmetler Ust Gorseli
              </button>
              <button
                type="button"
                onClick={() => openEditorByPath("home.hero.videoSrc", "video")}
                className="w-full bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
              >
                Ana Sayfa Arka Plan Videosu
              </button>
              <button
                type="button"
                onClick={() => openEditorByPath("home.brands.logos", "array")}
                className="w-full bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
              >
                Ana Sayfa Marka Logolari (Ekle/Sil)
              </button>
              <button
                type="button"
                onClick={() => openEditorByPath("contactPage.brandsSection.brands", "array")}
                className="w-full bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
              >
                Iletisim Marka Logolari (Ekle/Sil)
              </button>
            </div>

            <div className="text-xs text-zinc-400 space-y-2">
              <p>Onizlemede bir alana tiklayin. Alan popup editorunde acilir.</p>
              <p>Liste alanlarinda yeni oge ekleyebilir, silebilirsiniz.</p>
            </div>
          </>
        ) : (
          <div className="text-xs text-zinc-400 space-y-2">
            <p>Blog yazilarini bu menuden form ile yonetin.</p>
            <p>JSON gormeden yeni yazi ekleyebilir ve mevcut yazilari duzenleyebilirsiniz.</p>
          </div>
        )}
      </aside>

      <main className="flex-1">
        {mode === "preview" ? (
          <iframe
            key={previewPath}
            ref={iframeRef}
            src={`${previewPath}${previewPath.includes("?") ? "&" : "?"}cmsPreview=1`}
            className="w-full h-full border-0"
            title="Canli Onizleme"
          />
        ) : (
          <div className="h-full overflow-y-auto p-6">
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Blog Yonetimi</h2>
                  <p className="text-zinc-400 text-sm">Buradan JSON gormeden blog yazisi ekleyebilir, duzenleyebilir ve silebilirsiniz.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={addBlogPost}
                    className="bg-red-600 hover:bg-red-700 px-4 py-2 text-sm font-semibold"
                  >
                    Yeni Blog Ekle
                  </button>
                  <button
                    type="button"
                    onClick={deleteSelectedBlog}
                    disabled={!selectedBlogPost}
                    className="bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 px-4 py-2 text-sm"
                  >
                    Secili Blogu Sil
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-6">
                <div className="bg-zinc-900 border border-zinc-800 p-3 space-y-2 h-fit max-h-[78vh] overflow-y-auto">
                  {blogPosts.length ? (
                    blogPosts.map((post) => (
                      <button
                        key={post.id}
                        type="button"
                        onClick={() => setSelectedBlogId(post.id)}
                        className={`w-full text-left px-3 py-3 border text-sm transition-colors ${selectedBlogId === post.id ? "bg-red-600/20 border-red-600/70 text-zinc-100" : "bg-zinc-950 border-zinc-800 hover:border-zinc-600 text-zinc-300"}`}
                      >
                        <p className="font-semibold leading-tight">{post.title || "Basliksiz yazi"}</p>
                        <p className="text-xs text-zinc-400 mt-1">{post.slug || "-"}</p>
                      </button>
                    ))
                  ) : (
                    <p className="text-zinc-400 text-sm px-2 py-1">Henuz blog yazisi yok.</p>
                  )}
                </div>

                <div className="bg-zinc-900 border border-zinc-800 p-5 space-y-6">
                  {selectedBlogPost ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-zinc-400 mb-2">Baslik</label>
                          <input
                            className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
                            value={selectedBlogPost.title}
                            onChange={(e) => updateSelectedBlog((current) => ({ ...current, title: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-400 mb-2">Slug</label>
                          <div className="flex gap-2">
                            <input
                              className="flex-1 bg-zinc-950 border border-zinc-700 px-3 py-2"
                              value={selectedBlogPost.slug}
                              onChange={(e) => updateSelectedBlog((current) => ({ ...current, slug: e.target.value }))}
                            />
                            <button
                              type="button"
                              onClick={() => updateSelectedBlog((current) => ({ ...current, slug: slugify(current.title) || current.slug }))}
                              className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs"
                            >
                              Slug Uret
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-400 mb-2">Kategori</label>
                          <input
                            className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
                            value={selectedBlogPost.category}
                            onChange={(e) => updateSelectedBlog((current) => ({ ...current, category: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-400 mb-2">Okuma Suresi</label>
                          <input
                            className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
                            value={selectedBlogPost.readTime}
                            onChange={(e) => updateSelectedBlog((current) => ({ ...current, readTime: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-400 mb-2">Gorunen Tarih</label>
                          <input
                            className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
                            value={selectedBlogPost.date}
                            onChange={(e) => updateSelectedBlog((current) => ({ ...current, date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-400 mb-2">Yayin Tarihi (ISO)</label>
                          <input
                            className="w-full bg-zinc-950 border border-zinc-700 px-3 py-2"
                            value={selectedBlogPost.publishedAt}
                            onChange={(e) => updateSelectedBlog((current) => ({ ...current, publishedAt: e.target.value }))}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-zinc-400 mb-2">Ozet</label>
                        <textarea
                          className="w-full min-h-24 bg-zinc-950 border border-zinc-700 px-3 py-2"
                          value={selectedBlogPost.excerpt}
                          onChange={(e) => updateSelectedBlog((current) => ({ ...current, excerpt: e.target.value }))}
                        />
                      </div>

                      <div className="space-y-3">
                        <label className="block text-xs text-zinc-400">Kapak Gorseli</label>
                        {selectedBlogPost.image ? (
                          <div className="bg-zinc-950 border border-zinc-700 p-3">
                            <img src={selectedBlogPost.image} alt={selectedBlogPost.title} className="w-full max-h-72 object-cover" />
                          </div>
                        ) : null}
                        <input
                          ref={blogImageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            await uploadSelectedBlogImage(file);
                            e.currentTarget.value = "";
                          }}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => blogImageInputRef.current?.click()}
                            className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-sm"
                          >
                            Gorsel Yukle
                          </button>
                          <input
                            className="flex-1 bg-zinc-950 border border-zinc-700 px-3 py-2"
                            value={selectedBlogPost.image}
                            onChange={(e) => updateSelectedBlog((current) => ({ ...current, image: e.target.value }))}
                            placeholder="Gorsel URL"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold">Blog Icerigi (Paragraflar)</h3>
                          <button
                            type="button"
                            onClick={() => updateSelectedBlog((current) => ({ ...current, content: [...current.content, "Yeni paragraf"] }))}
                            className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs"
                          >
                            Paragraf Ekle
                          </button>
                        </div>
                        {selectedBlogPost.content.map((paragraph, paragraphIndex) => (
                          <div key={`content-${paragraphIndex}`} className="space-y-2">
                            <textarea
                              className="w-full min-h-20 bg-zinc-950 border border-zinc-700 px-3 py-2"
                              value={paragraph}
                              onChange={(e) =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  content: current.content.map((item, idx) => (idx === paragraphIndex ? e.target.value : item)),
                                }))
                              }
                            />
                            <button
                              type="button"
                              onClick={() =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  content: current.content.filter((_, idx) => idx !== paragraphIndex),
                                }))
                              }
                              className="text-xs text-red-400 hover:text-red-300"
                            >
                              Paragrafi Sil
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold">One Cikan Notlar</h3>
                          <button
                            type="button"
                            onClick={() => updateSelectedBlog((current) => ({ ...current, highlights: [...current.highlights, "Yeni not"] }))}
                            className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs"
                          >
                            Not Ekle
                          </button>
                        </div>
                        {selectedBlogPost.highlights.map((item, highlightIndex) => (
                          <div key={`highlight-${highlightIndex}`} className="flex gap-2">
                            <input
                              className="flex-1 bg-zinc-950 border border-zinc-700 px-3 py-2"
                              value={item}
                              onChange={(e) =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  highlights: current.highlights.map((currentItem, idx) => (idx === highlightIndex ? e.target.value : currentItem)),
                                }))
                              }
                            />
                            <button
                              type="button"
                              onClick={() =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  highlights: current.highlights.filter((_, idx) => idx !== highlightIndex),
                                }))
                              }
                              className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs"
                            >
                              Sil
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold">SSS</h3>
                          <button
                            type="button"
                            onClick={() => updateSelectedBlog((current) => ({ ...current, faqs: [...current.faqs, { q: "Yeni soru?", a: "Yeni cevap." }] }))}
                            className="bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs"
                          >
                            Soru Ekle
                          </button>
                        </div>
                        {selectedBlogPost.faqs.map((faq, faqIndex) => (
                          <div key={`faq-${faqIndex}`} className="bg-zinc-950 border border-zinc-700 p-3 space-y-2">
                            <input
                              className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2"
                              value={faq.q}
                              onChange={(e) =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  faqs: current.faqs.map((currentFaq, idx) => (idx === faqIndex ? { ...currentFaq, q: e.target.value } : currentFaq)),
                                }))
                              }
                              placeholder="Soru"
                            />
                            <textarea
                              className="w-full min-h-20 bg-zinc-900 border border-zinc-700 px-3 py-2"
                              value={faq.a}
                              onChange={(e) =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  faqs: current.faqs.map((currentFaq, idx) => (idx === faqIndex ? { ...currentFaq, a: e.target.value } : currentFaq)),
                                }))
                              }
                              placeholder="Cevap"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                updateSelectedBlog((current) => ({
                                  ...current,
                                  faqs: current.faqs.filter((_, idx) => idx !== faqIndex),
                                }))
                              }
                              className="text-xs text-red-400 hover:text-red-300"
                            >
                              SSS Ogesini Sil
                            </button>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-zinc-400 text-sm">Blog listesi bos. Yeni bir yazi ekleyin.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {mode === "preview" && selection ? (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-zinc-900 border border-zinc-700 p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-xs text-zinc-400">Düzenlenen alan</p>
                <h2 className="text-lg font-semibold break-all">{selection.path}</h2>
              </div>
              <button className="text-zinc-400 hover:text-zinc-100" onClick={() => setSelection(null)}>
                Kapat
              </button>
            </div>

            {nearestArrayPath && nearestArrayPath !== selection.path ? (
              <button
                type="button"
                onClick={openNearestArrayEditor}
                className="mb-4 bg-zinc-800 hover:bg-zinc-700 px-3 py-2 text-xs"
              >
                Bu listede ekle/sil yap ({nearestArrayPath})
              </button>
            ) : null}

            <ValueEditor
              value={editingValue}
              type={selectedType}
              onChange={setEditingValue}
              onUpload={uploadImage}
            />

            <div className="flex items-center gap-3 mt-5">
              <button onClick={applyEditorValue} className="bg-red-600 hover:bg-red-700 px-4 py-2 text-sm">
                Alanı Güncelle
              </button>
              <button
                onClick={() => setSelection(null)}
                className="bg-zinc-800 hover:bg-zinc-700 px-4 py-2 text-sm"
              >
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
